import { parse as parseWithSourceMap } from 'json-source-map';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  SchemaDefinition,
  schemaManager,
} from '@/features/schemaValidation/schema-manager';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import type { ValidationError } from '@/types';

export interface UseSchemaValidationProps {
  stepType?: VorgangProzessSchritt;
  debounceMs?: number;
  onValidationChange?: (errors: ValidationError[], isValid: boolean) => void;
}

export interface UseSchemaValidationReturn {
  validationErrors: ValidationError[];
  isValid: boolean;
  isValidating: boolean;
  validateJson: (jsonString: string) => void;
  clearValidation: () => void;
  getSchemaDefinition: () => SchemaDefinition | undefined;
}

export const useSchemaValidation = ({
  stepType,
  debounceMs = 300,
  onValidationChange,
}: UseSchemaValidationProps = {}): UseSchemaValidationReturn => {
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [isValid, setIsValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Find the best location for an error using json-source-map
  const findErrorLocation = useCallback(
    (
      errorPath: string[],
      sourceMap: unknown,
    ): { line: number; column: number } => {
      // Try to find the exact path in the source map
      let currentPointer = '';
      let bestLocation = { line: 1, column: 1 };

      // Build the JSON pointer path step by step and find the deepest match
      for (const pathSegment of errorPath) {
        const newPointer = currentPointer + '/' + pathSegment;

        // Check if this path exists in the source map
        const typedSourceMap = sourceMap as {
          pointers?: Record<
            string,
            {
              value?: { line: number; column: number };
              key?: { line: number; column: number };
            }
          >;
        };
        if (typedSourceMap?.pointers?.[newPointer]) {
          const pointer = typedSourceMap.pointers[newPointer];
          bestLocation = {
            line: pointer.value?.line + 1 || pointer.key?.line + 1 || 1,
            column: pointer.value?.column + 1 || pointer.key?.column + 1 || 1,
          };
          currentPointer = newPointer;
        } else {
          // If the exact path doesn't exist, we've found the deepest parent
          break;
        }
      }

      return bestLocation;
    },
    [],
  );

  const createValidationErrorFromZodIssue = useCallback(
    (zodIssue: unknown, sourceMap?: unknown): ValidationError => {
      const path = (zodIssue as { path: string[]; message: string }).path.join(
        '.',
      );
      const message = (zodIssue as { path: string[]; message: string }).message;

      // Try to find the line and column using source map
      let line = 1;
      let column = 1;

      if (sourceMap && (zodIssue as { path: string[] }).path.length > 0) {
        const location = findErrorLocation(
          (zodIssue as { path: string[] }).path,
          sourceMap,
        );
        line = location.line;
        column = location.column;
      }

      return {
        line,
        column,
        message: path ? `${path}: ${message}` : message,
        path,
        severity: 'error' as const,
      };
    },
    [findErrorLocation],
  );

  const createValidationError = useCallback(
    (
      errorMessage: string,
      index: number,
      sourceMap?: unknown,
    ): ValidationError => {
      // Parse error message to extract path and message
      const pathMatch = errorMessage.match(/^([^:]+):\s*(.+)$/);
      const path = pathMatch ? pathMatch[1] : `validation-${index}`;
      const message = pathMatch ? pathMatch[2] : errorMessage;

      // Try to find the line and column using source map
      let line = 1;
      let column = 1;

      if (sourceMap && path && path !== `validation-${index}`) {
        const errorPath = path.split('.').filter(Boolean);
        const location = findErrorLocation(errorPath, sourceMap);
        line = location.line;
        column = location.column;
      }

      return {
        line,
        column,
        message: `${path}: ${message}`,
        path,
        severity: 'error' as const,
      };
    },
    [findErrorLocation],
  );

  const createSyntaxError = useCallback(
    (parseError: SyntaxError, jsonString: string): ValidationError => {
      const match = parseError.message.match(/position (\d+)/);
      const position = match ? parseInt(match[1]) : 0;
      const lines = jsonString.substring(0, position).split('\n');
      const line = lines.length;
      const column = lines[lines.length - 1].length + 1;

      return {
        line,
        column,
        message: `JSON Syntax Error: ${parseError.message}`,
        path: 'syntax',
        severity: 'error',
      };
    },
    [],
  );

  const validateJson = useCallback(
    (jsonString: string) => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      setValidationErrors([]);
      setIsValid(true);
      setIsValidating(true);

      const timeoutId = setTimeout(() => {
        const errors: ValidationError[] = [];
        let sourceMap: unknown = null;
        let parsed;

        try {
          // Parse JSON with source map for accurate line/column tracking
          const parseResult = parseWithSourceMap(jsonString);
          parsed = parseResult.data;
          sourceMap = parseResult;

          // Schema validation with source map context
          if (stepType) {
            try {
              const validation = schemaManager.validateStepPayload(
                parsed,
                stepType,
              );

              if (!validation.isValid) {
                // Use Zod issues directly if available for better accuracy
                if (validation.zodIssues && validation.zodIssues.length > 0) {
                  const schemaErrors = validation.zodIssues.map((zodIssue) =>
                    createValidationErrorFromZodIssue(zodIssue, sourceMap),
                  );
                  errors.push(...schemaErrors);
                } else {
                  // Fallback to string error messages
                  const schemaErrors = validation.errors.map(
                    (errorMessage, index) =>
                      createValidationError(errorMessage, index, sourceMap),
                  );
                  errors.push(...schemaErrors);
                }
              }
            } catch (validationError) {
              errors.push({
                message: `Validation error: ${validationError}`,
                path: 'unknown',
                severity: 'error' as const,
                line: 1,
                column: 1,
              });
            }
          }
        } catch (parseError) {
          if (parseError instanceof SyntaxError) {
            errors.push(createSyntaxError(parseError, jsonString));
          } else {
            errors.push({
              message: 'Unknown parsing error',
              path: 'unknown',
              severity: 'error',
              line: 1,
              column: 1,
            });
          }
        }

        const newIsValid = errors.length === 0;
        setValidationErrors(errors);
        setIsValid(newIsValid);
        setIsValidating(false);
        validationTimeoutRef.current = null;

        // Notify parent of validation changes
        onValidationChange?.(errors, newIsValid);
      }, debounceMs);

      validationTimeoutRef.current = timeoutId;
    },
    [
      debounceMs,
      stepType,
      createValidationError,
      createValidationErrorFromZodIssue,
      createSyntaxError,
      onValidationChange,
    ],
  );

  // Clear validation state
  const clearValidation = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }
    setValidationErrors([]);
    setIsValid(true);
    setIsValidating(false);
  }, []);

  // Get current schema definition
  const getSchemaDefinition = useCallback(() => {
    if (stepType) {
      return schemaManager.getSchemaForStep(stepType);
    }
    return undefined;
  }, [stepType]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  return {
    validationErrors,
    isValid,
    isValidating,
    validateJson,
    clearValidation,
    getSchemaDefinition,
  };
};
